import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { ResetPasswordDialog } from "@/components/users/ResetPasswordDialog";
import { UserApiKeysPanel } from "@/components/users/UserApiKeysPanel";
import { UserFormDialog } from "@/components/users/UserFormDialog";
import { UsersTable } from "@/components/users/UsersTable";
import type { UserOut } from "@/lib/api";
import { useAuthStore } from "@/state/authStore";
import { useUsersStore } from "@/state/usersStore";

function UserKpis({ users }: { users: UserOut[] }) {
  const total = users.length;
  const active = users.filter((u) => u.is_active).length;
  const admins = users.filter((u) => u.is_admin).length;

  const cards = [
    { label: "Total de usuários", value: String(total) },
    { label: "Ativos", value: String(active) },
    { label: "Administradores", value: String(admins) },
  ];

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {cards.map((c) => (
        <div
          key={c.label}
          className="flex flex-col gap-1 rounded-lg border border-ink-700 bg-ink-850 px-5 py-4"
        >
          <span className="text-xs font-medium uppercase tracking-wide text-ink-400">
            {c.label}
          </span>
          <span className="text-2xl font-semibold tabular-nums text-ink-100">{c.value}</span>
        </div>
      ))}
    </div>
  );
}

export function Users() {
  const shellUser = useAuthStore((s) => s.user);
  const bootstrapSession = useAuthStore((s) => s.bootstrapSession);

  const users = useUsersStore((s) => s.users);
  const loading = useUsersStore((s) => s.loading);
  const fetchError = useUsersStore((s) => s.error);
  const selectedUserId = useUsersStore((s) => s.selectedUserId);
  const fetchUsers = useUsersStore((s) => s.fetchUsers);
  const setSelectedUserId = useUsersStore((s) => s.setSelectedUserId);
  const createUser = useUsersStore((s) => s.createUser);
  const updateUser = useUsersStore((s) => s.updateUser);
  const resetPassword = useUsersStore((s) => s.resetPassword);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [editTarget, setEditTarget] = useState<UserOut | null>(null);
  const [resetTarget, setResetTarget] = useState<UserOut | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (shellUser?.is_admin) void fetchUsers();
  }, [fetchUsers, shellUser?.is_admin]);

  if (!shellUser?.is_admin) {
    return (
      <section className="flex h-full min-h-0 flex-col overflow-auto px-6 py-6">
        <h1 className="text-2xl font-semibold tracking-tight text-ink-100">Usuários</h1>
        <p className="mt-2 max-w-xl text-sm text-ink-300">
          Esta área é restrita a administradores. Se precisar de acesso, fale com o admin da instância.
        </p>
        <Link
          to="/manager"
          className="mt-6 inline-flex w-fit rounded border border-ink-600 px-4 py-2 text-sm font-medium text-ink-200 hover:bg-ink-800"
        >
          Voltar ao File Manager
        </Link>
      </section>
    );
  }

  const openCreate = () => {
    setFormMode("create");
    setEditTarget(null);
    setFormOpen(true);
  };

  const openEdit = (u: UserOut) => {
    setFormMode("edit");
    setEditTarget(u);
    setFormOpen(true);
  };

  const handleFormSubmit = async (payload: {
    name: string;
    email: string;
    password?: string;
    is_active: boolean;
    is_admin: boolean;
  }) => {
    if (formMode === "create") {
      await createUser({
        name: payload.name,
        email: payload.email,
        password: payload.password ?? "",
        is_active: payload.is_active,
        is_admin: payload.is_admin,
      });
      return;
    }
    if (!editTarget) return;
    await updateUser(editTarget.id, {
      name: payload.name,
      is_active: payload.is_active,
      is_admin: payload.is_admin,
    });
    if (editTarget.id === shellUser.id) {
      await bootstrapSession();
    }
  };

  const handleResetPasswordSubmit = async (password: string) => {
    if (!resetTarget) return;
    await resetPassword(resetTarget.id, { password });
    if (resetTarget.id === shellUser.id) {
      await bootstrapSession();
    }
  };

  const handleToggleActive = async (u: UserOut) => {
    setActionError(null);
    try {
      await updateUser(u.id, { is_active: !u.is_active });
      if (u.id === shellUser.id) {
        await bootstrapSession();
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Não foi possível alterar o status.");
    }
  };

  const selectedUser = selectedUserId
    ? (users.find((u) => u.id === selectedUserId) ?? null)
    : null;

  return (
    <section className="flex h-full min-h-0 flex-col overflow-auto px-6 py-6">
      <div className="flex shrink-0 flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink-100">Usuários</h1>
          <p className="mt-2 max-w-3xl text-sm text-ink-300">
            Gestão de contas, perfis administrativos e API keys nesta instância.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500"
        >
          Novo usuário
        </button>
      </div>

      <div className="mt-6 shrink-0 space-y-4">
        <UserKpis users={users} />

        {fetchError ? (
          <p className="text-sm text-red-400" role="alert">
            {fetchError}
          </p>
        ) : null}
        {actionError ? (
          <p className="text-sm text-red-400" role="alert">
            {actionError}
          </p>
        ) : null}
      </div>

      <div className="mt-4 flex min-h-[12rem] flex-1 flex-col">
        <UsersTable
          users={users}
          loading={loading}
          selectedUserId={selectedUserId}
          currentUserId={shellUser.id}
          onSelectUser={setSelectedUserId}
          onEdit={openEdit}
          onResetPassword={setResetTarget}
          onToggleActive={(u) => void handleToggleActive(u)}
        />
      </div>

      {selectedUser ? (
        <div className="mt-6 shrink-0 pb-4">
          <p className="text-xs text-ink-500">
            Painel para <span className="text-ink-300">{selectedUser.name}</span> (
            {selectedUser.email}). Clique novamente na linha para ocultar.
          </p>
          <UserApiKeysPanel userId={selectedUser.id} />
        </div>
      ) : null}

      <UserFormDialog
        open={formOpen}
        mode={formMode}
        user={formMode === "edit" ? editTarget : null}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
      />

      <ResetPasswordDialog
        open={resetTarget !== null}
        user={resetTarget}
        onClose={() => setResetTarget(null)}
        onSubmit={(pwd) => handleResetPasswordSubmit(pwd)}
      />
    </section>
  );
}
